import React from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { styled } from 'ml-rubiks-styleguide/src/styled';

import { useCustomerData } from '../../contexts/customer-data-context';
import { useI18n } from '../../contexts/i18n-context';
import { usePaymentState } from '../../contexts/payment-state-context';
import { PaymentProcessor } from '../../core/payment-processor';
import { useProviderInitialization } from '../../hooks/use-provider-initialization';
import { PaymentMethod } from '../../types';
import { AvailablePaymentMethodsV1Dto } from '../../types/api';
import { isFieldVisible } from '../../utils/customer-fields-helpers';
import {
    BacsConsentCheckbox,
    DirectDebitSignatureSection,
    LoadingMessage,
    MandateDownloadButton,
    UnifiedError
} from '../common';

const ProviderContainer = styled('div')<{
    $hidden?: boolean;
    $hiddenBacsFields?: { holderName?: boolean; email?: boolean };
}>(({ $hidden, $hiddenBacsFields }) => ({
    ...($hidden && { display: 'none' }),
    '& .adyen-checkout__field--consentCheckbox': {
        display: 'none'
    },
    ...($hiddenBacsFields?.holderName && {
        '& .adyen-checkout__bacs--holder-name': {
            display: 'none'
        }
    }),
    ...($hiddenBacsFields?.email && {
        '& .adyen-checkout__bacs--shopper-email': {
            display: 'none'
        }
    }),
    '&[data-bacs-consent-given="false"] .adyen-checkout__button--pay': {
        opacity: 0.5,
        pointerEvents: 'none'
    },
    '&[data-bacs-consent-given="true"] .adyen-checkout__button--pay': {
        opacity: 1,
        pointerEvents: 'auto'
    }
}));

interface ProviderBasedPaymentProps {
    methodType: PaymentMethod;
    paymentProcessor: PaymentProcessor;
    availablePaymentMethods?: AvailablePaymentMethodsV1Dto[];
    onPaymentSuccess?: () => void;
}

export const ProviderBasedPayment: React.FC<ProviderBasedPaymentProps> = ({
    methodType,
    paymentProcessor,
    availablePaymentMethods,
    onPaymentSuccess
}) => {
    const { paymentState } = usePaymentState();
    const { customerData } = useCustomerData();
    const { t } = useI18n();

    const {
        containerRef,
        isInitializing,
        isInitialized,
        isFormValid,
        requireSignature,
        setBacsConsent,
        setSignature,
        providerConfig,
        paymentProvider,
        mandateDownloadData,
        completeMandateDownload,
        paymentStopReason,
        isRetrying,
        retryPayment
    } = useProviderInitialization({
        methodType,
        paymentProcessor,
        availablePaymentMethods,
        onPaymentSuccess
    });

    const hidePaymentButton = paymentProcessor.getConfig()?.hidePaymentButton;

    const showMandateDownload = mandateDownloadData !== null;

    const showBacsConsent =
        methodType === 'BACS' &&
        isInitialized &&
        paymentProvider === 'ADYEN' &&
        !showMandateDownload;

    const showAchSignature =
        methodType === 'ACH' &&
        isInitialized &&
        paymentProvider === 'ADYEN' &&
        requireSignature &&
        !showMandateDownload;

    const handleSubmit = () => {
        void providerConfig?.submit?.();
    };

    const isAdyenBacs = methodType === 'BACS' && paymentProvider === 'ADYEN';

    const showGoCardlessProcessing =
        methodType === 'BACS' &&
        paymentProvider === 'GOCARDLESS' &&
        isInitialized &&
        paymentState.isProcessing &&
        !showMandateDownload;

    const hiddenBacsFields = isAdyenBacs
        ? {
              holderName: !isFieldVisible(customerData?.name),
              email: !isFieldVisible(customerData?.email)
          }
        : undefined;

    return (
        <>
            {isInitializing && (
                <FlexBox padding={8} hAlign="center">
                    <LoadingMessage />
                </FlexBox>
            )}
            {paymentStopReason && (
                <UnifiedError
                    severity={
                        paymentStopReason === 'canceled' ? 'warning' : 'error'
                    }
                    message={
                        paymentStopReason === 'canceled'
                            ? t('upc.payment.canceled', 'Payment was canceled.')
                            : t(
                                  'upc.payment.failed',
                                  'Payment could not be completed.'
                              )
                    }
                    onRetry={() => {
                        void retryPayment();
                    }}
                    retrying={isRetrying}
                />
            )}
            {showGoCardlessProcessing && (
                <FlexBox padding={8} hAlign="center">
                    <LoadingMessage />
                </FlexBox>
            )}
            <ProviderContainer
                ref={containerRef}
                className="payment-provider-container"
                $hidden={
                    showMandateDownload ||
                    showGoCardlessProcessing ||
                    paymentStopReason !== null
                }
                $hiddenBacsFields={hiddenBacsFields}
            />
            {showBacsConsent && (
                <BacsConsentCheckbox onChange={setBacsConsent} />
            )}
            {showAchSignature && (
                <DirectDebitSignatureSection
                    onSignatureChange={setSignature}
                    onSubmit={handleSubmit}
                    isProcessing={paymentState.isProcessing}
                    hideButton={hidePaymentButton}
                    disabled={!isFormValid}
                    buttonLabel="Continue"
                />
            )}
            {showMandateDownload && (
                <MandateDownloadButton
                    mandateUrl={mandateDownloadData.mandateUrl}
                    onComplete={completeMandateDownload}
                />
            )}
        </>
    );
};
